import { ReactNode, useRef } from 'react';
import * as Ariakit from '@ariakit/react';
import { css, styled } from 'styled-components';
import { AnimatePresence, motion } from 'motion/react';

import { Container } from '../Layout';
import { MenuCloseIcon, PlusIcon } from '../../assets/icons';
import { Text } from '../Text';
import { DefaultIconProps } from '../../utils';
import { unsetButtonStyle } from '../../utils/unsetButtonStyle';

type ContentDisclosureProps = {
  titleOpen: string;
  titleClose: string;
  initiallyExpanded?: boolean;
  children: ReactNode;
  hideClose?: boolean;
};

export const ContentDisclosure = ({
  titleOpen,
  titleClose,
  initiallyExpanded = false,
  hideClose = false,
  children,
}: ContentDisclosureProps) => {
  const store = Ariakit.useDisclosureStore({ defaultOpen: initiallyExpanded });
  const mounted = Ariakit.useStoreState(store, 'mounted');
  const elementRef = useRef<HTMLDivElement | null>(null);

  return (
    <Container>
      <Ariakit.DisclosureProvider store={store}>
        <AnimatePresence>
          {mounted && (
            <Ariakit.DisclosureContent
              // So height can be animated when closing
              alwaysVisible
              render={
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ bounce: 0, duration: 0.2 }}
                  ref={elementRef}
                  onAnimationStart={() => {
                    if (elementRef.current) {
                      elementRef.current.style.overflow = 'hidden';
                    }
                  }}
                  onAnimationComplete={() => {
                    if (elementRef.current) {
                      elementRef.current.style.overflow = 'unset';
                    }
                  }}
                />
              }
            >
              {children}
            </Ariakit.DisclosureContent>
          )}
        </AnimatePresence>
        {!mounted && <ToggleButton title={titleOpen} buttonType="open" />}
        {mounted && !hideClose && <ToggleButton title={titleClose} buttonType="close" />}
      </Ariakit.DisclosureProvider>
    </Container>
  );
};

const iconProps: Partial<DefaultIconProps> = { marginRight: 6, width: 14, color: 'foraBlue' };

const ToggleButton = ({ title, buttonType }: { title: string; buttonType: 'open' | 'close' }) => (
  <AriaDisclosure $buttonType={buttonType}>
    {buttonType === 'close' ? <MenuCloseIcon {...iconProps} /> : <PlusIcon {...iconProps} />}
    <Text color="foraBlue" size="medium">
      {title}
    </Text>
  </AriaDisclosure>
);

const AriaDisclosure = styled(Ariakit.Disclosure)<{ $buttonType: 'open' | 'close' }>`
  ${unsetButtonStyle}
  cursor: pointer;
  display: flex;
  align-items: center;

  ${({ $buttonType }) =>
    $buttonType === 'close' &&
    css`
      margin-top: 10px;
    `}
`;
